import { useEffect, useRef, useState } from "react";
import { Egg, FileText, Info, Trophy, Volume2, VolumeX, Zap } from "lucide-react";
import { useWindows } from "@/os/window/context";
import { useSettings } from "@/os/state/settings";
import { useAchievements } from "@/os/state/achievements";
import { useToast, type ToastItem } from "@/os/state/toast";
import { DESKTOP_ICONS, getDef } from "@/os/window/registry";
import { useLocalStorage } from "@/hooks";
import { setSoundEnabled } from "@/lib/sound";
import { withBreaks } from "@/components/decor";

function ToastCard({ t, onClose }: { t: ToastItem; onClose: () => void }) {
    useEffect(() => {
        const timer = setTimeout(onClose, 4600);
        return () => clearTimeout(timer);
    }, [onClose]);
    const Icon = t.kind === "achievement" ? Trophy : t.kind === "egg" ? Egg : t.kind === "mode" ? Zap : Info;
    const color = t.kind === "achievement" ? "#EAB308" : t.kind === "egg" ? "#A855F7" : t.kind === "mode" ? "#FF6B6B" : "#2563EB";
    return (
        <div className="os-toast pop-in" data-testid={`toast-${t.kind}`}>
            <Icon size={16} style={{ color, flex: "none", marginTop: 2 }} />
            <div className="flex-1">
                <p className="t-title">{t.title}</p>
                {t.body && <p className="t-body">{t.body}</p>}
            </div>
            <button data-testid="toast-close" aria-label="Dismiss notification" className="text-inkfaint hover:text-ink" onClick={onClose}>
                ×
            </button>
        </div>
    );
}

function StartItem({ id, onOpen }: { id: string; onOpen: () => void }) {
    const def = getDef(id);
    if (!def) return null;
    const Icon = def.icon;
    return (
        <button data-testid={`start-item-${id}`} className="os-icon !gap-1 !py-2" onClick={onOpen}>
            <span className="grid h-9 w-9 place-items-center rounded-lg border-2 border-ink" style={{ background: def.tileBg, color: def.color }}>
                <Icon size={16} />
            </span>
            <span className="os-icon-label !text-[9px]">{withBreaks(def.label ?? def.title)}</span>
        </button>
    );
}

export function Taskbar() {
    const { windows, focusedId, open, focus, close } = useWindows();
    const { modes } = useSettings();
    const { unlockedCount, trackEgg } = useAchievements();
    const { toasts, push, dismiss } = useToast();
    const [soundOn, setSoundOn] = useLocalStorage("krithios:sound", false);
    const [startOpen, setStartOpen] = useState(false);
    const [now, setNow] = useState(() => new Date());
    const [logoWig, setLogoWig] = useState(false);
    const logoPokes = useRef(0);
    const clockPokes = useRef(0);

    const pokeLogo = () => {
        logoPokes.current += 1;
        setLogoWig(true);
        setTimeout(() => setLogoWig(false), 400);
        if (logoPokes.current === 5) {
            trackEgg("egg_logo_poker");
            push("system", "the K has feelings.", "five pokes. it wiggles accordingly.");
        }
    };

    const pokeClock = () => {
        clockPokes.current += 1;
        if (clockPokes.current === 3) {
            trackEgg("egg_clock_watcher");
            push("system", "it's just a clock.", "it has been keeping time the whole time. respect it.");
        }
    };

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 15000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        setSoundEnabled(soundOn);
    }, [soundOn]);

    return (
        <>
            <div
                className="pointer-events-none fixed bottom-20 right-3 z-[4500] flex flex-col items-end gap-2"
                role="status"
                aria-live="polite"
                data-testid="toast-stack"
            >
                {toasts.map((t) => (
                    <ToastCard key={t.id} t={t} onClose={() => dismiss(t.id)} />
                ))}
            </div>

            <nav className="taskbar no-print" data-testid="taskbar" aria-label="krithi.os taskbar">
                <div className="relative">
                    <button
                        data-testid="taskbar-start"
                        aria-label="All apps"
                        aria-expanded={startOpen}
                        className={`relative z-[4000] grid h-9 w-9 place-items-center rounded-xl border-2 border-ink bg-elec ${logoWig ? "wiggle" : ""}`}
                        onClick={() => {
                            pokeLogo();
                            setStartOpen((v) => !v);
                        }}
                    >
                        <span className="font-display text-lg font-extrabold text-white">K</span>
                    </button>
                    {startOpen && (
                        <>
                            <button
                                className="fixed inset-0 z-[3995] cursor-default"
                                aria-label="Close menu"
                                data-testid="start-backdrop"
                                onClick={() => setStartOpen(false)}
                            />
                            <div
                                className="pop-in absolute bottom-12 left-0 z-[4200] w-[300px] rounded-2xl border-2 border-ink bg-paper p-3 shadow-[5px_5px_0_#1C1917]"
                                data-testid="start-menu"
                            >
                                <p className="px-1 pb-2 font-mono text-[10px] text-inkfaint">all apps</p>
                                <div className="os-icons !grid-cols-3">
                                    {[...DESKTOP_ICONS, "achievements"].map((id) => (
                                        <StartItem
                                            key={id}
                                            id={id}
                                            onOpen={() => {
                                                setStartOpen(false);
                                                open(id);
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <button
                    data-testid="taskbar-resume"
                    className="grid h-9 w-9 place-items-center rounded-xl border-2 border-ink bg-sagesoft"
                    aria-label="Open resume"
                    onClick={() => open("resume_pdf")}
                >
                    <FileText size={16} className="text-sage" />
                </button>

                {modes.chaotic && <span className="badge animate-pulse border-coral bg-coralsoft" data-testid="chaos-badge">CHAOS MODE</span>}
                {modes.bedtime && <span className="badge border-lav bg-lavsoft" data-testid="bedtime-badge">bedtime conflict</span>}
                {modes.impatient && <span className="badge border-coral bg-coralsoft" data-testid="impatient-badge">impatient</span>}

                <div className="flex max-w-[300px] items-center gap-1.5 overflow-x-auto os-scroll sm:max-w-[420px]">
                    {windows.map((w) => (
                        <span key={w.id} className={`taskbar-chip ${focusedId === w.id && !w.minimized ? "active" : ""} ${w.minimized ? "opacity-60" : ""}`}>
                            <button data-testid={`taskbar-chip-${w.id}`} className="max-w-[110px] truncate" onClick={() => focus(w.id)}>
                                {w.title}
                            </button>
                            <button
                                data-testid={`taskbar-chip-close-${w.id}`}
                                aria-label={`Close ${w.title}`}
                                className="text-inkfaint hover:text-coral"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    close(w.id);
                                }}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>

                <button
                    className="ml-auto hidden font-mono text-[11px] tabular-nums text-inksoft sm:inline"
                    data-testid="taskbar-clock"
                    aria-label="System clock"
                    title="it's just a clock"
                    onClick={pokeClock}
                >
                    {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </button>

                <button
                    data-testid="taskbar-sound"
                    aria-label={soundOn ? "Mute sounds" : "Enable sounds"}
                    aria-pressed={soundOn}
                    className="grid h-9 w-9 place-items-center rounded-xl border-2 border-ink bg-paper"
                    onClick={() => setSoundOn((v) => !v)}
                >
                    {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} className="text-inkfaint" />}
                </button>

                <button
                    data-testid="taskbar-achievements"
                    aria-label="Achievements"
                    className="relative grid h-9 w-9 place-items-center rounded-xl border-2 border-ink bg-butter"
                    onClick={() => open("achievements")}
                >
                    <Trophy size={16} />
                    <span className="absolute -right-1.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full border border-ink bg-coral px-1 font-mono text-[9px] text-white">
                        {unlockedCount}
                    </span>
                </button>
            </nav>
        </>
    );
}
